// Enhanced Approval Workflow Types

export enum ApprovalStatus {
  Submitted = 'Submitted',
  UnderReview = 'UnderReview',
  Verification = 'Verification',
  AdditionalInfoRequired = 'AdditionalInfoRequired',
  FinalReview = 'FinalReview',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Suspended = 'Suspended'
}

export enum ComplianceCheckType {
  BusinessLicense = 'BusinessLicense',
  InsuranceCertificate = 'InsuranceCertificate',
  LocationVerification = 'LocationVerification',
  PhotoSubmissions = 'PhotoSubmissions',
  ServicePricing = 'ServicePricing',
  ContactInformation = 'ContactInformation'
}

export enum DocumentType {
  BusinessLicense = 'BusinessLicense',
  InsuranceCertificate = 'InsuranceCertificate',
  TaxIdDocument = 'TaxIdDocument',
  BarbershopPhotos = 'BarbershopPhotos',
  OwnerIdentification = 'OwnerIdentification',
  LeaseAgreement = 'LeaseAgreement',
  OperatingPermit = 'OperatingPermit'
}

export interface EnhancedBarbershopApproval {
  id: number;
  barbershopId: number;
  barbershopName: string;
  ownerName: string;
  ownerEmail: string;
  address: string;
  phoneNumber: string;
  website?: string;
  description?: string;
  imageUrls: string[];

  status: ApprovalStatus;
  previousStatus: ApprovalStatus;
  statusDisplayName: string;
  statusColor: string;

  submittedAt: string;
  reviewStartedAt?: string;
  verificationStartedAt?: string;
  finalReviewStartedAt?: string;
  completedAt?: string;

  assignedReviewer?: string;
  lastUpdatedBy?: string;
  lastUpdatedAt: string;

  notes?: string;
  rejectionReason?: string;
  additionalInfoRequested?: string;
  additionalInfoRequestedAt?: string;

  slaDeadline?: string;
  isOverdue: boolean;
  daysInReview: number;

  complianceChecks: ComplianceCheck[];
  documents: ApprovalDocument[];
  communications: ApprovalCommunication[];
  statusHistory: ApprovalStatusHistory[];
  progress: ApprovalProgress;
}

export interface ComplianceCheck {
  id: number;
  checkType: ComplianceCheckType;
  checkName: string;
  description?: string;
  isRequired: boolean;
  isCompleted: boolean;
  isPassed: boolean;
  failureReason?: string;
  checkedBy?: string;
  checkedAt?: string;
  notes?: string;
  createdAt: string;
}

export interface ApprovalDocument {
  id: number;
  documentType: DocumentType;
  documentName: string;
  filePath: string;
  mimeType?: string;
  fileSizeBytes: number;
  isRequired: boolean;
  isVerified: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
  expiresAt?: string;
  verificationNotes?: string;
  uploadedBy: string;
  uploadedAt: string;
  version: number;
  isExpired: boolean;
}

export interface ApprovalCommunication {
  id: number;
  fromUserId: string;
  fromUserName: string;
  toUserId?: string;
  toUserName?: string;
  subject: string;
  message: string;
  isInternal: boolean;
  isEmailSent: boolean;
  emailSentAt?: string;
  emailError?: string;
  createdAt: string;
}

export interface ApprovalStatusHistory {
  id: number;
  fromStatus: ApprovalStatus;
  toStatus: ApprovalStatus;
  fromStatusDisplayName: string;
  toStatusDisplayName: string;
  changedBy: string;
  changedByName: string;
  reason?: string;
  notes?: string;
  changedAt: string;
}

export interface ApprovalProgress {
  currentStep: number;
  totalSteps: number;
  progressPercentage: number;
  steps: ApprovalStep[];
  estimatedTimeRemaining: string; // TimeSpan as ISO 8601 duration string
}

export interface ApprovalStep {
  stepNumber: number;
  stepName: string;
  status: ApprovalStatus;
  isCompleted: boolean;
  isCurrent: boolean;
  completedAt?: string;
  completedBy?: string;
}

// Action DTOs
export interface UpdateApprovalStatusDto {
  newStatus: ApprovalStatus;
  reason?: string;
  notes?: string;
  additionalInfoRequested?: string;
  assignedReviewer?: string;
}

export interface SendCommunicationDto {
  toUserId?: string;
  subject: string;
  message: string;
  isInternal: boolean;
  sendEmail: boolean;
}

export interface VerifyDocumentDto {
  documentId: number;
  isVerified: boolean;
  verificationNotes?: string;
}

export interface BatchApprovalActionDto {
  workflowIds: number[];
  action: ApprovalStatus;
  reason?: string;
  notes?: string;
}
